/* eslint-disable max-depth */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, ProgressCircle } from 'antd-mobile';
import semver from 'semver';
import { Device, DeviceInfo } from '@capacitor/device';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useDocumentVisibility, useGetState, useLatest } from 'ahooks';
import { App } from '@capacitor/app';
import { selectCurrentServingOrder } from '@/store/service';
import { setApkUpdateStatus, setApkVersion } from '@/store/device';
import { isNewSoftwareVersion, Toast } from '@/utils';
import service from '@/service';
import { SlideModal } from '@/components';
import { storagePrefixV2 } from '@/constant';
import cx from './index.module.scss';

interface Props {}

/** 展示更新提示的页面 */
const SHOW_UPDATE_MODAL_PATHNAME = ['/home', '/user/member-detail', '/service/add-service'];

/**
 * 判断APK是否有更新
 */
const ApkUpdater: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const location = useLocation();
  const [isDialogVisible, setIsDialogVisible, getIsDialogVisible] = useGetState(false);
  const [isSlideModalVisible, setIsSlideModalVisible] = useState(false);
  const downloadUrlRef = useRef(null);
  const [shouldWaitRobotOrderFinish, setShouldWaitRobotOrderFinish] = useState(false);
  const domVisibility = useDocumentVisibility();
  const isDownloadingApk = useRef(false);
  const [appInfo, setAppInfo, getAppInfo] = useGetState<{
    platform: DeviceInfo['platform'];
    appVersion: string;
  }>({
    platform: null,
    appVersion: null,
  });

  const [progress, setProgress] = useState(0);

  /** 当前是否在AI问询流程 */
  const currentIsInAIConsultationProcess = useLatest(
    useMemo(() => location?.pathname?.includes('ai-consultation'), [location?.pathname])
  );

  const confrimBtnDom = useMemo(
    () =>
      progress === 0 ? (
        '点击下载'
      ) : progress === 100 ? (
        '确认安装'
      ) : (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>下载中</span>
          <ProgressCircle
            percent={progress}
            style={{
              '--track-width': '2px',
              '--size': '20px',
              '--track-color': '#2d6efa',
              '--fill-color': '#fff',
              marginLeft: '8px',
            }}
          />
        </div>
      ),
    [progress]
  );

  const initAppInfo = useCallback(async () => {
    try {
      const deviceInfo = await Device.getInfo();

      if (deviceInfo.platform !== 'android') {
        dispatch(setApkUpdateStatus('not-update'));
        return;
      }

      const appInfo = await App.getInfo();
      setAppInfo({
        platform: deviceInfo?.platform,
        appVersion: appInfo?.version,
      });
      setApkVersion(appInfo?.version);
    } catch (e) {
      dispatch(setApkUpdateStatus('not-update'));
      console.log('获取设备信息失败', e);
    }
  }, [dispatch, setAppInfo]);

  const checkApkUpdateStatus = useCallback(async () => {
    if (typeof window === 'undefined') {
      dispatch(setApkUpdateStatus('not-update'));
      return;
    }

    // 当前更新弹窗已打开
    if (getIsDialogVisible()) {
      return;
    }

    if (getAppInfo().platform === 'android') {
      try {
        const { data } = await service.system.getLatestAPK();
        if (!data?.downloadUrl) {
          dispatch(setApkUpdateStatus('not-update'));
          return;
        }

        const currentAppVersion = getAppInfo()?.appVersion;
        downloadUrlRef.current = data.downloadUrl;

        if (
          semver.valid(data?.version) &&
          semver.valid(currentAppVersion) &&
          semver.gt(data?.version, currentAppVersion)
        ) {
          // APP在1.15.0开始支持应用内更新APK
          if (isNewSoftwareVersion(currentAppVersion, '1.15.0')) {
            // 在AI问诊流程时需要等AI问诊流程结束后再提示更新
            if (currentIsInAIConsultationProcess.current) {
              setShouldWaitRobotOrderFinish(true);
              dispatch(setApkUpdateStatus('not-update'));
              return;
            }

            try {
              // 判断当前是否已有下载完成的APK文件且文件版本是否一致
              const downloadedInfo = await window.ApkUpdater?.getDownloadedUpdate();

              if (downloadedInfo?.app?.version?.name === data?.version) {
                // 版本一致，直接使用该安装包
                setProgress(100);
              } else {
                await window.ApkUpdater?.reset();
              }
            } catch (e) {
              console.log('本地无已下载完成的安装包');
            } finally {
              // 判断当前是否有机器人订单
              const { data } = await service.service.getServingOrderByOperator();
              if (data?.step === 'RoboticTask') {
                setShouldWaitRobotOrderFinish(true);
                dispatch(setApkUpdateStatus('not-update'));
              } else {
                setIsDialogVisible(true);
                dispatch(setApkUpdateStatus('should-update'));
              }
            }
          } else {
            // 兼容1.15.0以下版本APK的更新逻辑
            Dialog.clear();
            Dialog.alert({
              title: '检测到更新',
              content: '我们对现有产品进行了修复和优化，请尽快下载安装更新。',
              confirmText: '确认安装',
              onConfirm() {
                window.open(data.downloadUrl);
                return Promise.reject('');
              },
            });
          }
        } else {
          dispatch(setApkUpdateStatus('not-update'));
        }
      } catch (e) {
        dispatch(setApkUpdateStatus('not-update'));
        console.log('校验apk版本信息失败', e);
      }
    }
  }, [currentIsInAIConsultationProcess, dispatch, getAppInfo, getIsDialogVisible, setIsDialogVisible]);

  const handleClickConfirm = useCallback(async () => {
    if (progress === 0) {
      // 当前正在下载安装包，忽略
      if (isDownloadingApk.current) {
        return;
      }

      try {
        isDownloadingApk.current = true;
        await window.ApkUpdater?.download(downloadUrlRef.current, {
          onDownloadProgress: info => setProgress(info?.progress),
        });
        window.ApkUpdater?.install();
      } catch (e) {
        Toast.failIconCode('下载失败，请确认网络连接后重试');
        window.ApkUpdater?.reset();
        setProgress(0);
      } finally {
        isDownloadingApk.current = false;
      }
    } else if (progress === 100) {
      window.ApkUpdater?.install();
    }
  }, [progress]);

  useEffect(() => {
    // 当前是否需要等机器人订单结束后提示更新
    if (!shouldWaitRobotOrderFinish) {
      return;
    }
    if (currentServingOrder?.step !== 'RoboticTask' && SHOW_UPDATE_MODAL_PATHNAME.includes(location?.pathname)) {
      setIsDialogVisible(true);
      dispatch(setApkUpdateStatus('should-update'));
    } else {
      setIsDialogVisible(false);
      dispatch(setApkUpdateStatus('not-update'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.step, location?.pathname, shouldWaitRobotOrderFinish]);

  useEffect(() => {
    initAppInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appInfo?.appVersion && appInfo?.platform && domVisibility === 'visible') {
      checkApkUpdateStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domVisibility, appInfo]);

  return (
    <div className={cx('ApkUpdater')}>
      <Dialog
        bodyClassName="custom-dialog-body-dom"
        title="检测到更新"
        content={
          <div>
            软件版本过低，需升级后才可正常运行
            <br />
            请尽快下载安装更新
          </div>
        }
        visible={isDialogVisible}
        actions={[
          {
            key: 'cancel',
            text: '安装指引',
            onClick: () => {
              setIsSlideModalVisible(true);
              setIsDialogVisible(false);
            },
          },
          {
            key: 'confirm',
            text: confrimBtnDom,
            onClick: () => {
              handleClickConfirm();
            },
          },
        ]}
      />
      <SlideModal open={isSlideModalVisible} className={cx('apk-updater-slide-modal')}>
        <div className={cx('wrapper')}>
          <div className={cx('header')}>
            <div>平板更新安装指导</div>
            <div
              className={cx('confirm-text')}
              onClick={() => {
                setIsSlideModalVisible(false);
                setIsDialogVisible(true);
              }}
            >
              完成
            </div>
          </div>
          <div className={cx('content')}>
            <img src={storagePrefixV2 + '/apk-update-tips.png'} alt="" />
          </div>
        </div>
      </SlideModal>
    </div>
  );
};

export default ApkUpdater;
